import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const RequestsGrid = ({
  requests,
  selectedRequests,
  onSelectRequest,
  onSelectAll,
  onViewDetails,
  onUpdateStatus
}) => {
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-warning/10 text-warning border-warning/20',
      'in-progress': 'bg-primary/10 text-primary border-primary/20',
      completed: 'bg-success/10 text-success border-success/20',
      cancelled: 'bg-muted text-muted-foreground border-border'
    };
    return colors?.[status] || colors?.pending;
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: 'Clock',
      'in-progress': 'Activity',
      completed: 'CheckCircle2',
      cancelled: 'XCircle'
    };
    return icons?.[status] || 'Circle';
  };

  const getUrgencyColor = (urgency) => {
    return urgency === 'Emergency' ?'bg-destructive/10 text-destructive border-destructive/20' :'bg-secondary/10 text-secondary border-secondary/20';
  };

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const sortedRequests = [...requests]?.sort((a, b) => {
    if (sortConfig?.key) {
      const aValue = a?.[sortConfig?.key];
      const bValue = b?.[sortConfig?.key];
      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const SortIcon = ({ columnKey }) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ChevronsUpDown" size={14} color="var(--color-muted-foreground)" strokeWidth={2} />;
    }
    return sortConfig?.direction === 'asc'
      ? <Icon name="ChevronUp" size={14} color="var(--color-primary)" strokeWidth={2} />
      : <Icon name="ChevronDown" size={14} color="var(--color-primary)" strokeWidth={2} />;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  if (requests?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-12 text-center">
        <Icon name="Inbox" size={48} className="mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Service Requests</h3>
        <p className="text-sm text-muted-foreground">No service requests match your current filters.</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left">
                <Checkbox
                  checked={selectedRequests?.length === requests?.length && requests?.length > 0}
                  onChange={(e) => onSelectAll(e?.target?.checked)}
                />
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('request_number')}
                  className="flex items-center space-x-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-hover"
                >
                  <span>Request ID</span>
                  <SortIcon columnKey="request_number" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('driver_phone')}
                  className="flex items-center space-x-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-hover"
                >
                  <span>Driver</span>
                  <SortIcon columnKey="driver_phone" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('created_at')}
                  className="flex items-center space-x-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-hover"
                >
                  <span>Submitted</span>
                  <SortIcon columnKey="created_at" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('issue_type')}
                  className="flex items-center space-x-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-hover"
                >
                  <span>Issue Type</span>
                  <SortIcon columnKey="issue_type" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('urgency')}
                  className="flex items-center space-x-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-hover"
                >
                  <span>Urgency</span>
                  <SortIcon columnKey="urgency" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Technician</span>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center space-x-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-hover"
                >
                  <span>Status</span>
                  <SortIcon columnKey="status" />
                </button>
              </th>
              <th className="px-4 py-3 text-right">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedRequests?.map((request) => (
              <tr
                key={request?.id}
                className="hover:bg-muted/30 transition-colors"
              >
                <td className="px-4 py-3">
                  <Checkbox
                    checked={selectedRequests?.includes(request?.id)}
                    onChange={(e) => onSelectRequest(request?.id, e?.target?.checked)}
                  />
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm font-medium text-foreground">{request?.request_number}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col">
                    <span className="text-sm text-foreground">{request?.driver_phone}</span>
                    <span className="text-xs text-muted-foreground">Unit: {request?.unit_number}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col">
                    <span className="text-sm text-foreground">{formatDate(request?.created_at)}</span>
                    <span className="text-xs text-muted-foreground">{formatTime(request?.created_at)}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-foreground">{request?.issue_type}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-md text-xs font-medium border ${getUrgencyColor(request?.urgency)}`}>
                    <Icon
                      name={request?.urgency === 'Emergency' ? 'AlertTriangle' : 'Calendar'}
                      size={12}
                      strokeWidth={2}
                    />
                    <span>{request?.urgency}</span>
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-muted-foreground">Unassigned</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-md text-xs font-medium border ${getStatusColor(request?.status)}`}>
                    <Icon name={getStatusIcon(request?.status)} size={12} strokeWidth={2} />
                    <span className="capitalize">{request?.status?.replace('-', ' ')}</span>
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="ghost"
                      size="xs"
                      iconName="Eye"
                      onClick={() => onViewDetails(request)}
                      title="View Details"
                    />
                    {request?.status === 'pending' && (
                      <Button
                        variant="ghost"
                        size="xs"
                        iconName="Play"
                        onClick={() => onUpdateStatus(request?.id, 'in-progress')}
                        title="Start Progress"
                      />
                    )}
                    {request?.status === 'in-progress' && (
                      <Button
                        variant="ghost"
                        size="xs"
                        iconName="CheckCircle2"
                        onClick={() => onUpdateStatus(request?.id, 'completed')}
                        title="Mark Complete"
                      />
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Info */}
      <div className="px-4 py-3 border-t border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing <span className="font-medium text-foreground">{sortedRequests?.length}</span> of{' '}
            <span className="font-medium text-foreground">{sortedRequests?.length}</span> requests
          </p>
          <div className="flex items-center space-x-2">
            <Icon name="Info" size={14} color="var(--color-primary)" strokeWidth={2} />
            <span className="text-xs text-muted-foreground">Real-time updates enabled</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestsGrid;